import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Router } from '@angular/router';
import { forkJoin, of } from "rxjs";

import { PermissionService } from '@core/services/permission.service';
import { UserService } from '@core/services/user.service';
import { CustomEventService } from '@shared/services/custom-event.service';
import { DatasourceService } from '@shared/services/datasource.service';
import { DeveloperService } from '@shared/services/developer.service';
import { TeamService } from '@shared/services/team.service';
import { OrganizationService } from '@shared/services/organization.service';
import { AddDeveloperModalComponent } from '@shared/components/modals/add-developer-modal/add-developer-modal.component';
import { AddTeamModalComponent } from '@shared/components/modals/add-team-modal/add-team-modal.component';
import { DeveloperListModalComponent } from '@shared/components/modals/developer-list-modal/developer-list-modal.component';
import { InitializeScorecardModalComponent } from '@shared/components/modals/initialize-scorecard-modal/initialize-scorecard-modal.component';

import ActionTypesEnum from '@shared/enums/action-constant.enum';
import { CompanyRoleEnum } from '@shared/enums/company-role.enum';
import { BaseSkeletonEnum } from '@shared/enums/skeleton.enum';

import { TeamScorecard } from "@shared/models/team-scorecard";

import { DEFAULT_PAGE_SIZE } from '@shared/variables/constant-variable';

import { getUserLimitations } from '@shared/utils/limitation.util';
import {
  backgroundImageCreator,
  fullNamePropertyCreator,
  truncateArray,
  truncateTeams
} from '@shared/utils/object-mapper.util';
import { getModalContent, getScorecardStep } from '@shared/utils/scorecard.util';
import { separateDisabledUsers, sortArrayAlphabetically, sortFieldsOnTable,sortArrayAlphabeticallyDevelopers } from '@shared/utils/sort.util';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { StorageService } from '@core/services/storage.service';
import { AdminService } from "@shared/services/admin.service";
import { PropertiesService } from "@core/services/properties.service";
import { HttpResponse } from "@angular/common/http";

@UntilDestroy()
@Component({
  selector: 'app-teams-health',
  templateUrl: './teams-health.component.html'
})
export class TeamsHealthComponent implements OnInit {

  PAGE_SIZE = DEFAULT_PAGE_SIZE;

  loading: boolean;
  isScorecardId: boolean;
  developers: Developer.DeveloperModel[];
  teams: Team.TeamModel[];
  editableTeams: string[];
  editableDevelopers: Developer.DeveloperModel[];
  developersDisplayData: Developer.DeveloperModel[] = [];
  originalDevelopersData: Developer.DeveloperModel[] = [];
  teamsDisplayData: Team.TeamModel[] = [];
  teamActiveProfiles: Team.TeamModel[] = [];
  activeProfile: Developer.DeveloperModel;

  activeDeveloperCount:any;
  inactiveDeveloperCount:any;

  addDeveloperModal: NzModalRef;
  addTeamModal: NzModalRef;
  developerListModal: NzModalRef;
  teamScorecardModal: NzModalRef;
  baseSkeletonEnum = BaseSkeletonEnum;

  // sort, search, filter
  searchValue = '';
  sortValue: Base.KeyValueModel;
  filterValue: string[] = [];
  profilesTeamList: { value: string, text: string, byDefault?: boolean }[];
  branchValues: any[] = [];

  activeUser: ActiveUser.ActiveUserModel;
  userLimited: User.Limitations;

  @ViewChild('toolTipTitle', { static: true }) toolTipTitle: TemplateRef<any>;
  @ViewChild('noDataSourceTemplate') noDataSourceTemplate: TemplateRef<{}>;
  tooltipContent: any;

  // tab index
  selectedIndex = 0;
  showManageProfileColumns: boolean;

  level: OrganizationModel.Organization[];

  mapOfExpandedData: { [key: string]: Team.TeamModel[] } = {};

  createButtonCase: any;
  licenseLimitations: LicenseLimitations.LicenseLimitationsModel;

  allTeams: any;
  pageDevIndex: any;
  pageTeamIndex: any;

  individualScorecard: any;
  gmOneScorecard: any;
  featuresData: any;
  showManageTeamUpdatableColumns: boolean;
  updatableTeam: string;
  updatable: boolean;
  expandedData: any;
  addNewTeamModal: boolean;
  developersForTeams: any;
  profileTeamFilter: any[];
  isButtonDisabled = false;
  visible = false;
  listOfLeads: {
    selected?: boolean;
    value: string, text: string, byDefault?: boolean
  }[];
  allLeads: any;
  allTools: any;
  listOfTools: {
    selected?: any;
    value: string, text: string, byDefault?: boolean
  }[];
  teamHealthTeams: any;
  sortValueForTeams: any;
  leadsOfTeams: any;
  unselectedDevelopers: Developer.DeveloperModel[];
  exportButtonLoading :boolean;
  teamLeads: any;
  developersListFromTable:any;

  constructor(
    private customEventService: CustomEventService,
    private modalService: NzModalService,
    private notificationService: NzNotificationService,
    private teamService: TeamService,
    private developerService: DeveloperService,
    private userService: UserService,
    private permissionService: PermissionService,
    private organizationService: OrganizationService,
    private datasourceService: DatasourceService,
    private storageService: StorageService,
    private adminService: AdminService,
    private modal: NzModalService,
    private router: Router,
    public propertiesService: PropertiesService
  ) {

    this.selectedIndex = +this.storageService.get('teams-health-tab');
    if (!this.selectedIndex) {
      this.storageService.set('teams-health-tab', '0');
    }

    this.pageDevIndex = this.storageService.get('active_dev_page');
    if (this.pageDevIndex === null || this.pageDevIndex === undefined) {
      this.pageDevIndex = 1;
    }

    this.pageTeamIndex = this.storageService.get('active_team_page');
    if (this.pageTeamIndex === null || this.pageTeamIndex === undefined) {
      this.pageTeamIndex = 1;
    }
  }

  static mapper(item: any): any {
    item = backgroundImageCreator(item);

    if (item.teams) {
      item = fullNamePropertyCreator(item);
      item.displayTeams = truncateArray(item.teams, 'name');
    }

    if (item.leads || item.children) {
      item.displayLeads = truncateArray(item.leads, 'name');

      // tslint:disable-next-line:no-shadowed-variable
       const addLeads = (item) => {
         for (const i of item.children) {
           i.displayLeads = truncateArray(i.leads, 'name');
           if (i.children) {
             addLeads(i)
           }
         }
      }
      addLeads(item);
    }

    if (item.developers) {
      item.displayDevelopers = truncateArray(item.developers, 'name');
    }

    if (item.companyRole) {
      item.displayCompanyRole = CompanyRoleEnum[item.companyRole];
    }

    if (item.addonAccounts) {
      item.addonAccountsTooltip = item.addonAccounts.join('<br>');
    }

    return item;
  }

  ngOnInit(): void {
    getUserLimitations(this.userService).then(response => {
      this.licenseLimitations = response;
    }).catch(error => {
      console.log('error while limitations data', error);
    });

    this.activeUser = this.userService.activeUser;

    this.loadInitialData();
    this.levelInit();
    if (this.activeUser.limitedEdition) {
      getUserLimitations(this.userService).then(response => {
        this.userLimited = response;
      }).catch(error => {
        console.log('error while limitations data', error);
      });
    }
    this.getIndividualScorecardActive();
    // Subscribe to the dashboard announcements to update
    this.customEventService.eventAnnounced$.pipe(
      untilDestroyed(this)
    ).subscribe(({ action, data }) => {
      if (action === ActionTypesEnum.CLOSE_MODAL) {
        if (this.addDeveloperModal) {
          this.addDeveloperModal.close();
        }

        if (this.teamScorecardModal) {
          this.teamScorecardModal.close();
        }

        if (this.addTeamModal) {
          this.addTeamModal.close();
        }

        if (this.developerListModal) {
          this.developerListModal.close();
        }
      }

      if (action === ActionTypesEnum.CREATE_DEVELOPER) {
        this.createDeveloper(data);
      }

      if (action === ActionTypesEnum.UPDATE_DEVELOPER) {
        this.updateDeveloper(data);
      }

      if (action === ActionTypesEnum.UPDATE_DEVELOPER_POPUP) {
        this.addDeveloper(data);
      }

      if (action === ActionTypesEnum.DELETE_DEVELOPER_POPUP) {
        this.delete(data);
      }

      if (action === ActionTypesEnum.CREATE_TEAM) {
        this.addTeamModal.close();
        this.createTeam(data);
      }

      if (action === ActionTypesEnum.UPDATE_TEAM) {
        this.addTeamModal.close();
        this.updateTeam(data);
      }

      if (action === ActionTypesEnum.NEXT_STEP_DATA) {
        getScorecardStep({
          stepIndex: data.stepIndex,
          modalContent: this.teamScorecardModal.getContentComponent(),
          key: data.key,
          teamId: data.teamId,
          teamService: this.teamService,
          customEventService: this.customEventService
        });
      }

      if (action === ActionTypesEnum.CREATE_TEAM_SCORECARD) {
        this.createTeamScorecard(data);
      }

      if (action === ActionTypesEnum.GET_BRANCH_DATA) {
        this.getBranchDataForInitializeScoreCard(data);
      }
    });

    this.createButtonCase = this.selectedIndex === 1 ? this.developers?.length >= this.userLimited?.developer &&
      (this.permissionService.isSuperUser || this.permissionService.isAdmin
        || (this.activeProfile && (this.activeProfile.companyRole === 'TEAM_LEAD' || this.activeProfile.companyRole === 'MANAGER' || this.activeProfile.companyRole === 'DIRECTOR')))
      : this.teams?.length >= this.userLimited?.team && (this.permissionService.isSuperUser || this.permissionService.isAdmin);
  }

  collapse(array: Team.TeamModel[], data: Team.TeamModel, $event: boolean): void {
    if (!$event) {
      if (data.children) {
        data.children.forEach(team => {
          // tslint:disable-next-line:no-non-null-assertion
          const target = array.find(item => item.key === team.key)!;
          target.expand = false;
          this.collapse(array, target, false);
        });
      } else {
        return;
      }
    }
  }

  convertTreeToList(root: Team.TeamModel): Team.TeamModel[] {
    const stack: Team.TeamModel[] = [];
    const array: Team.TeamModel[] = [];
    const hashMap = {};
    stack.push({ ...root, level: 0, expand: this.isNodeExpanded(root) });

    while (stack.length !== 0) {
      // tslint:disable-next-line:no-non-null-assertion
      const node = stack.pop()!;
      this.visitNode(node, hashMap, array);
      if (node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          // tslint:disable-next-line:no-non-null-assertion
          const childNode = {...node.children[i], level: node.level! + 1, parent: node};
          childNode.expand = this.isNodeExpanded(childNode);
          stack.push(childNode);
        }
      }
    }
    return array;
  }

  isNodeExpanded(node: Team.TeamModel): boolean {
    const expandedData = JSON.parse(this.storageService.get('expandedData') || '{}');
    return expandedData[node.key] || false;
  }

  saveExpandedState(node: Team.TeamModel): void {
    const expandedData = JSON.parse(this.storageService.get('expandedData') || '{}');
    expandedData[node.key] = node.expand;

    if (!node.expand && node.children) {
      const reControlChild = (parent) => {
        if (parent.children.length > 0) {
          parent.children.forEach(item => (reControlChild(item)));
        }
        expandedData[parent.key] = false;
      }
      reControlChild(node);
    }
    this.storageService.set('expandedData', JSON.stringify(expandedData));
  }

  convertTreeToListExpand(root: Team.TeamModel): Team.TeamModel[] {
    const stack: Team.TeamModel[] = [];
    const array: Team.TeamModel[] = [];
    const hashMap = {};
    stack.push({...root, level: 0, expand: false});

    while (stack.length !== 0) {
      // tslint:disable-next-line:no-non-null-assertion
      const node = stack.pop()!;
      this.visitNode(node, hashMap, array);
      if (node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          // tslint:disable-next-line:no-non-null-assertion
          stack.push({...node.children[i], level: node.level! + 1, expand: false, parent: node});
        }
      }
    }
    return array;
  }

  visitNode(node: Team.TeamModel, hashMap: { [key: string]: boolean }, array: Team.TeamModel[]): void {
    if (!hashMap[node.key]) {
      hashMap[node.key] = true;
      array.push(node);
    }
  }

  sort($event: Base.KeyValueModel): void {
    this.sortValue = $event;
    this.search();
  }

  getLocalStorage() {
    const storedFilterValue = this.storageService.get('filterValue');
    if (storedFilterValue) {
      this.filterValue = JSON.parse(storedFilterValue);
      this.searchTeamProfile();
    }
  }

  filterChange($event: string[]): void {
    this.filterValue = $event;
    this.pageDevIndex = 1;
    this.storageService.set('filterValue', JSON.stringify($event))
    this.search()
  }

  setInitialSelections(): void {
    const storedValue = localStorage.getItem('filterValue');
    if (storedValue) {
      const parsedValue = JSON.parse(storedValue);
      if (parsedValue.length > 0) {
        parsedValue.forEach(selectedValue => {

          const selectedTeamItem = this.profilesTeamList.find(item => item.value === selectedValue);
          if (selectedTeamItem) {
            selectedTeamItem.byDefault = true;
          }
        });
      }
    }
  }

  clearButton(): void {
    this.filterValue = [];
    this.storageService.remove('filterValue');
    this.search();
    location.reload()
  }

  searchDropDownTeam() {
    this.visible = this.visible;
  }

  search(): void {
    this.searchForTeams(); // search yapmadan önce dropdown'dan filtrelenmiş dataların arasında çalışır.
    this.visible = false;
    const initialData: any[] = this.selectedIndex === 1
      ? (this.activeProfile ? this.developers.filter(item => item.id !== this.userService.activeUser.developerId) : this.developers)
      : this.teamsDisplayData; // dropdown'da filtre var,
    const filteredTeams = [];
    const filterFunc = (item: any) => {
      const isSearchValueDev = item.fullName?.toLowerCase().includes(this.searchValue.toLowerCase());
      const isSearchValue = item.name.toLowerCase().includes(this.searchValue.toLowerCase());
      if (isSearchValueDev) {
        filteredTeams.push(item);
      }

      if (isSearchValue && !isSearchValueDev) {
        if (this.searchValue.length > 0 && item.key) {
          item.key = item.key.replace("-", "");
        }
        filteredTeams.push(item);
      }
      const searchTeam = (team) => {
        for (const i of team.children) {
          const isSearchValueForChild = i.name.toLowerCase().includes(this.searchValue.toLowerCase());

          // burası gereksiz forEach yapılmış, her key için ayrı ayrı döngüye sokmuş gereksiz.

          // this.teamsDisplayData.forEach(j => {
          //   this.mapOfExpandedData[j.key] = this.convertTreeToListExpand(j);
          // });
          if (isSearchValueForChild) {
            if (this.searchValue.length > 0 && i.key) {
              i.key = i.key.replace("-", "");
            }
            const isAlreadyAdded = filteredTeams.some(t => t.id === i.id);
            if (!isAlreadyAdded) {
              filteredTeams.push(i);
            }
          }
          if (i.children) {
            searchTeam(i);
          }
        }
        return true;
      }

      if (((this.selectedIndex === 1
          ? (this.filterValue.length ? this.filterValue.some(filterId => item.teams.some(team => team.id === filterId)) : true)
          : true)
        && (item.fullName ? item.fullName.toLowerCase().includes(this.searchValue.toLowerCase())
          || item.userName.toLowerCase().includes(this.searchValue.toLowerCase())
          || item.addonAccounts.toString().toLowerCase().includes(this.searchValue.toLowerCase())
          : item.name.toLowerCase().includes(this.searchValue.toLowerCase()) || searchTeam(item))
      )) {
        return filteredTeams;
      }
      return null;
    };
    initialData.forEach(filterFunc);
    let data: any[] = filteredTeams;

    if (this.sortValue) {
      data = sortFieldsOnTable(data, {key: 'name', value: this.sortValue.value});
    }

    if (this.selectedIndex === 1) {
      this.developersDisplayData = separateDisabledUsers(data);
      this.originalDevelopersData = [...this.developersDisplayData]
    } else {
      this.teamsDisplayData = data;

      // expanded durumunu kontrol ettik 446. satırdaki olayı buraya taşıdık. Bir kere çalışmasını sağladık. Burası selectedIndex 0 için çalışır.
      this.teamsDisplayData.forEach(j => {
        this.mapOfExpandedData[j.key] = this.convertTreeToListExpand(j);
      });
    }

    if (this.searchValue === '') {
      // this.teamsDisplayData = this.teams;
      this.teamsDisplayData.forEach(item => {
        this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
      });
    }
    this.enabledDeveloperCount()
    this.disabledDeveloperCount()
    this.getLocalStorage()
    this.setInitialSelections()
  }

  searchTeamProfile(): void {
    const initialData: any[] = this.selectedIndex === 1
      ? this.developers.filter(item => item.id !== this.userService.activeUser.developerId)
      : this.teams;

    const filterFunc = (item: any) => {
      return (this.selectedIndex === 1
          ? (this.filterValue.length ? this.filterValue.some(filterId => item.teams.some(team => team.id === filterId)) : true)
          : true
      );
    };
    let data: any[] = initialData.filter(filterFunc);
    if (this.sortValue) {
      data = sortFieldsOnTable(data, {key: 'name', value: this.sortValue.value});
    }
    if (this.selectedIndex === 1) {
      this.developersDisplayData = separateDisabledUsers(data);
      if (this.filterValue.includes('unselected')){
       this.unselectedDevelopers = this.originalDevelopersData.filter(developer => developer.teams.length === 0);
        this.developersDisplayData = [
          ...this.developersDisplayData,
          ...this.unselectedDevelopers,
        ]
      } else {
        this.developersDisplayData = separateDisabledUsers(data);
      }
    } else {
      this.teamsDisplayData = data;
    }
  }



  loadInitialData(): void {
    this.loading = true;
    forkJoin([
      this.selectedIndex === 0 ? this.teamService.list() : of([]),
      this.selectedIndex === 1 ? this.developerService.list() : of([]),
      this.selectedIndex === 0 ? this.organizationService.teamHealthTeams() : of([]),
    ]).pipe(untilDestroyed(this)).subscribe(([allTeams, developers, teams]) => {
      this.loading = false;
      this.allTeams = sortArrayAlphabetically(allTeams, 'name');
      this.teamHealthTeams = sortArrayAlphabetically(teams, 'name');
      this.leadsOfTeams = this.collectLeads(this.teamHealthTeams);

      this.allLeads = [];
      this.leadsOfTeams.forEach(item => {
        const existingItem = this.allLeads.find(i => i.name === item.name);
        if (!existingItem) {
          this.allLeads.push(item);
        }
      });

      this.allTools = [];
      this.teamHealthTeams.forEach(item => {
        item.tools.forEach(team => {
          const existingItem = this.allTools.find(i => i.addonName === team.key);
          if (!existingItem) {
            this.allTools.push(team);
          }
        });
      });

      this.developers = sortArrayAlphabeticallyDevelopers(developers.map(TeamsHealthComponent.mapper), 'fullName')
      this.showManageProfileColumns = developers.some(item =>
        (this.userService.activeUser && item.id !== this.userService.activeUser.developerId) && item.clickable);
      this.activeProfile = developers.find(item => item.id === this.userService.activeUser.developerId);
      this.teams = sortArrayAlphabetically(teams.map(TeamsHealthComponent.mapper), 'name')
        .map(item => {

          // tslint:disable-next-line:no-shadowed-variable
          const addisEditable = (item) => {
            for (const i of item.children) {
              i.editable = item.updatable
              i.developerTooltip = i.teamActiveProfiles.activeProfiles.length !== 0
                ? (i.teamActiveProfiles.activeProfiles.length >= 15
                  ? i.teamActiveProfiles.activeProfiles.slice(0, 15).join('<br>') + '<br><a>More Details</a>'
                  : i.teamActiveProfiles.activeProfiles.join('<br>'))
                : ''
              if (i.children) {
                addisEditable(i)
              }
            }
          }
          addisEditable(item);
          return {
            ...item,
            editable: item.updatable,
            developerTooltip: item.teamActiveProfiles.activeProfiles.length !== 0
              ? (item.teamActiveProfiles.activeProfiles.length >= 15
                ? item.teamActiveProfiles.activeProfiles.slice(0, 15).join('<br>') + '<br><a>More Details</a>'
                : item.teamActiveProfiles.activeProfiles.join('<br>'))
              : '',
          }

        });

      if (this.activeProfile) {
        this.activeProfile = {
          ...this.activeProfile,
        };
        this.activeProfile.displayCompanyRole = CompanyRoleEnum[this.activeProfile.companyRole];
        this.editableTeams = this.activeProfile.teams.filter(team => team.lead).map(team => team.id)
          || this.activeProfile.teams.filter(team => team.director).map(team => team.id)
          || this.activeProfile.teams.filter(team => team.manager).map(team => team.id);
        this.editableDevelopers = this.developers
          .filter(item => item.clickable && item.id !== this.userService.activeUser.developerId)
          .map(item => fullNamePropertyCreator(item));
        this.activeProfile.customDisplayTeams = truncateTeams(this.activeProfile.teams.map(item => {
          const foundTeam = this.teams.find(team => team.id === item.id);

          return {
            id: item.id,
            name: item.name,
            scorecardId: foundTeam && foundTeam.scorecardId ? foundTeam.scorecardId : undefined
          }
        }));
      }
      this.teamsDisplayData = this.teams;
      this.profileTeamFilter = this.developers.map(data => data.teams.map(item => ({
        value: item.id,
        text: item.name
      })));

      this.profilesTeamList = [
        { value: 'unselected', text: 'Undefined Team(s)' },
      ];
      this.profileTeamFilter.forEach(teamArray => {
        teamArray.forEach(team => {
          const existingItem = this.profilesTeamList.find(i => i.text === team.text);
          if (!existingItem) {
            this.profilesTeamList.push(team);
          }
        });
      });
      this.sort({key: 'name', value: 'ascend'});

      this.listOfLeads = this.allLeads.map(item => {
        return {
          value: item.name,
          text: item.name
        }
      });

      this.listOfTools = this.allTools.map(item => {
        return {
          value: item.addonName,
          text: item.addonName
        }
      });

    }, error => {
      this.loading = false;
      console.log('error occurred while fetching initial data for Team Health', error);
    });
  }

  onDevelopersListUpdated(list: any[]) {
    this.developersListFromTable = list;
  }

  downloadFile() {
    this.exportButtonLoading = true;
    const filteredData = this.developersListFromTable.map(dev => ({
      name: dev.name,
      surname: dev.surname,
      userName: dev.userName,
      companyRole: dev.companyRole,
      status: dev.enabled,
      teams: dev.teams
    }));
    this.developerService.developersExportList(filteredData).subscribe(
      (response: HttpResponse<Blob>) => {
        this.exportButtonLoading = false;
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename=(.+)/);
          if (fileNameMatch && fileNameMatch.length > 1) {
            const fileName = fileNameMatch[1];
            const blob = new Blob([response.body], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
          }
        }
      },
      error => {
        console.error('Export failed', error)
        this.exportButtonLoading = true;
      }
    );
  }

  collectLeads(data, leads = []) {
    if (Array.isArray(data)) {
      data.forEach(item => this.collectLeads(item, leads));
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.leads)) {
        leads.push(...data.leads);
      }
      if (Array.isArray(data.children)) {
        this.collectLeads(data.children, leads);
      }
    }
    return leads;
  }


  addDeveloper(data?: Developer.DeveloperModel): void {
    if (this.isButtonDisabled) {
      return;
    }
    this.isButtonDisabled = true;

    if(this.developers.length >= this.licenseLimitations?.maxDeveloperCount && !data) {
      return;
    } else {
    forkJoin({
      fields: this.developerService.fields(),
      teams: this.teamService.list(),
    }).pipe(untilDestroyed(this))
      .subscribe(({fields, teams}: {fields: Developer.FieldsModel, teams: Team.TeamModel[]}) => {
        this.isButtonDisabled = false;
      if (fields && fields.companyRoles) {
        fields.companyRoles = sortArrayAlphabetically(fields.companyRoles, 'key');
      }

          const mode: string = data ? 'edit' : 'add';

          data = {
            ...data,
            teamsFieldEditable: this.permissionService.isAdmin || this.permissionService.isSuperUser
          };
          this.addDeveloperModal = this.modalService.create({
            nzMaskClosable: false,
            nzContent: AddDeveloperModalComponent,
            nzWidth: '650px',
            nzFooter: null,
            nzComponentParams: {
              data,
              mode,
              teams: !this.permissionService.isAdmin && !this.permissionService.isSuperUser
              && this.activeProfile && mode === 'add'
                ? teams.filter(item => this.editableTeams.indexOf(item.id) !== -1 && this.level.find(level => level.id === item.organizationLevelId && level.level >= 2))
                : teams.filter(team => this.level.find(level => level.id === team.organizationLevelId && level.level >= 2)),
              fields
            }
          });
        }, error => {
          console.log('error while fetching fields', error);
        });
    }
  }

  addTeam(data?: Team.TeamModel): void {
    if (this.isButtonDisabled) {
      return;
    }
    this.isButtonDisabled = true;

    if(!data && this.allTeams.length >= this.licenseLimitations?.maxTeamCount) {
      return;
    } else {
      if(data) {
        this.developerService.developerList(data.id).pipe(untilDestroyed((this))).subscribe(developerList => {
          data.developers = developerList
        })
      }
      forkJoin({
        developer: this.developerService.list(),
        teams: this.teamService.list()
      }).pipe(untilDestroyed(this))
        .subscribe(({developer, teams}: {developer: any, teams: Team.TeamModel[]}) => {
          this.isButtonDisabled = false;
          this.allTeams = sortArrayAlphabetically(teams, 'name');
          this.developers = sortArrayAlphabeticallyDevelopers(developer.map(TeamsHealthComponent.mapper), 'fullName')
          const mode = data ? 'edit' : 'add';
          if (!data) {
            data = {
              ...data,
              editable: true
            }
          }
          if (data.leads) {
            const roles = this.level.find(role => role.id === data.organizationLevelId).leadRoles.map(key => key.key);
            if (this.developers) {
              this.teamLeads = this.developers.filter(item =>
                roles.includes(item.companyRole)
              );

              data.leads = data.leads.filter(lead =>
                this.teamLeads.some(teamLead => teamLead.id === lead.id)
              );
            }
          }

          this.addTeamModal = this.modalService.create({
            nzMaskClosable: false,
            nzContent: AddTeamModalComponent,
            nzWidth: '650px',
            nzFooter: null,
            nzComponentParams: {
              data,
              mode,
              developers: this.developers
            }
          });
        }, error => {
          console.log('error while fetching fields', error);
        });
    }
  }

  createDeveloper(data: Developer.DeveloperModel[]): void {
    const innerComponent = this.addDeveloperModal.getContentComponent();

    innerComponent.loading = true;

    this.developerService.create(data)
      .pipe(untilDestroyed(this))
      .subscribe(_ => {
        innerComponent.loading = false;
        this.addDeveloperModal.close();

        this.loadInitialData();
      }, error => {
        innerComponent.loading = false;
        let content = error.message;
        if (error.status === 422) {
          content = 'The username already exists. Please use a different username!';
        }
        console.log('error occurred while creating developer', error);

        if (!error.error.message) {
          this.notificationService.create('error', 'Error', content);
        }
      });
  }

  updateDeveloper(data: Developer.DeveloperModel[]): void {
    this.developerService.update(data)
      .pipe(untilDestroyed(this))
      .subscribe(_ => {
        this.addDeveloperModal.close();
        this.loadInitialData();
      }, error => {
        console.log('error occurred while updating developer', error);
      });
  }

  createTeam(data: Team.TeamModel): void {
    const innerComponent = this.addTeamModal.getContentComponent();

    innerComponent.loading = true;

    this.teamService.create(data)
      .pipe(untilDestroyed(this))
      .subscribe(_ => {
        innerComponent.loading = false;

        this.loadInitialData();
      }, error => {
        innerComponent.loading = false;
        console.log('error occurred while creating team', error);
      });
  }

  updateTeam(data: Team.TeamModel): void {
    this.teamService.update(data)
      .pipe(untilDestroyed(this))
      .subscribe(_ => {
        this.loadInitialData();
      }, error => {
        console.log('error occurred while creating team', error);
      });
  }

  tabChange($event): void {
    this.selectedIndex = $event;
    this.storageService.set('teams-health-tab', $event)
    this.searchValue = '';
    this.pageDevIndex = 1;
    this.storageService.set('active_dev_page', "1");
    this.pageTeamIndex = 1;
    this.storageService.set('active_team_page', "1");
    this.loadInitialData();
    this.search();
    this.sort({key: 'name', value: 'ascend'});
  }

  delete(id: string): void {
    if (this.selectedIndex === 1) {
      this.developerService.delete(id)
        .pipe(untilDestroyed(this))
        .subscribe(() => {

          this.developersDisplayData = this.developersDisplayData.filter(item => item.id !== id);
          this.loadInitialData();
        }, error => {
          console.log('error while deleting developer', error);
        });
    } else {
      this.teamService.delete(id)
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          if(localStorage.getItem('filtered_teams') === id){
            localStorage.removeItem('filtered_teams')
          }
          this.teamsDisplayData = this.teamsDisplayData.filter(item => {
            return item.id !== id;
          });

          this.loadInitialData();
        }, error => {
          console.log('error while deleting team', error);
        });
    }
  }

  update(data: any): void {
    if (this.selectedIndex === 1) {
      this.addDeveloper(data);
    } else {
      this.addTeam(data);
    }
  }

  showDeveloperListModal(): void {
    this.developerListModal = this.modalService.create({
      nzMaskClosable: false,
      nzContent: DeveloperListModalComponent,
      nzComponentParams: {
        developers: this.editableDevelopers,
        type: 'developer',
        title: 'Developer List'
      },
      nzWidth: '650px',
      nzFooter: null
    });
  }

  levelInit(): void {
    this.organizationService.get()
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.level = response;
      }, error => {
        console.log('error while fetching values', error);
      });
  }

  initializeScorecardModal(team: any): void {
    const isTeam = this.level.find(item => team.organizationLevelId === item.id).level === 2;

    if (isTeam && !team.scorecardId && team.updatable) {
      this.teamService.initializeTabList()
        .pipe(untilDestroyed(this))
        .subscribe((steps) => {
          this.teamScorecardModal = this.modalService.create({
            nzMaskClosable: false,
            nzContent: InitializeScorecardModalComponent,
            nzComponentParams: {
              team,
              steps
            },
            nzWidth: '1033px',
            nzFooter: null
          });
          this.teamScorecardModal.afterOpen.pipe(untilDestroyed(this)).subscribe(() => {
            getModalContent(
              steps,
              this.teamScorecardModal.getContentComponent(),
              team.id,
              this.teamService,
              this.customEventService
            );
          });
        }, error => {
          if (error.error && error.error.message) {
            this.notificationService.template(this.noDataSourceTemplate);
          }
          console.log('error while fetching tab list', error);
        });
    } else if (team.clickable) {
      this.router.navigate(['/scorecard/team/' + team.id]);
    }
  }

  createTeamScorecard(data: { formData: TeamScorecard.CreateModel, teamId: string }): void {
    this.teamService.initializeScorecardCreate(data.formData)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.router.navigate(['scorecard', 'team', data.teamId]);
        // this.teamScorecardModal.close();
        this.customEventService.announceEvent(ActionTypesEnum.CREATE_TEAM_SCORECARD_SUCCESS);
      }, error => {
        this.customEventService.announceEvent(ActionTypesEnum.CREATE_TEAM_SCORECARD_ERROR, error);
      });
  }

  goToDataSource(): void {
    this.notificationService.remove();
    this.router.navigateByUrl('gitwiser');
  }

  upgradeConfirm(): void {
    window.open('https://oobeya.io/pricing', '_blank')
  }

  getBranchDataForInitializeScoreCard(data): void {
    if (!data.affect) {
      return;
    }
    const datasourceId = data.affect === 'project' ? data.value : (data.value.includes('$') ? (data.value.split('$')[0].includes(':') ? data.value.split('$')[0].split(':')[1] : data.value.split('$')[0]) : data.datasourceId);
    this.datasourceService.getFields(
      {
        datasourceId,
        affect: data.affect,
        project: (data.value.includes('$') ? data.value.split('$')[1] : data.value)
      })
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        let responseData = response?.map(item => ({
          ...item,
          selected:  item.selected
        }));

        if (data.affect && data.affect === 'project') {
          responseData.forEach(item => {
            if (!item.key.includes("datasource:")) {
              item.key = "datasource:" + data.value + "$" + item.key;
            }
          });
        }

        if (data.affect && data.affect === 'branch') {
          responseData.forEach(item => item.key = "datasource:" + datasourceId + "$" + item.key);
        }

        const modal = this.teamScorecardModal.getContentComponent();
        const currentStepIndex = data.currentStepIndex - 1;
        const stepToProcess = modal.steps[currentStepIndex];

        if (currentStepIndex < 3 ) {
            return;
        }

        const field = stepToProcess.fields[data.affect === 'project' ? 1 : 2];

        const removeItemWithKey = (list: any[], keyToRemove: string) => {
          return list.filter(item => item.key !== keyToRemove);
        };

        field.data.forEach(item => {
          responseData = removeItemWithKey(responseData, item.key);
        });

        field.data = [
          ...field.data,
          ...responseData
        ];
        if (data.affect === 'branch') {
          // for default select item in sonar branch (input[2])
          const sonarSelectedBranches= field.data.filter(sonar =>{
            return sonar.selected
          }).map(item =>item.key)
          modal.initializeForm.get(field.key).get('2').setValue([...sonarSelectedBranches]);
        }

      }, error => {
        console.log('error while fetching branches', error);
      });
  }

  upgradeLicense(): void {
    window.open('https://oobeya.io/contact-sales/', '_blank')
  }

  pageDevIndexChange($event): void {
    this.pageDevIndex = $event;
    this.storageService.set('active_dev_page', $event);
  }

  pageTeamIndexChange($event): void {
    this.pageTeamIndex = $event;
    this.storageService.set('active_team_page', $event);
  }

  getIndividualScorecardActive(): void {
    this.individualScorecard = JSON.parse(this.storageService.get('features_data_individual'));
    this.gmOneScorecard  = JSON.parse(this.storageService.get('features_data_gm1'));
  }

  enabledDeveloperCount() {
    return this.activeDeveloperCount = this.developersDisplayData.filter(item => item.enabled).length;
  }
  disabledDeveloperCount() {
    return this.inactiveDeveloperCount = this.developersDisplayData.filter(item => !item.enabled).length;
  }

  // filter and sort for teams table

  sortForTeams($event: Base.KeyValueModel): void {
    this.sortValueForTeams = $event;
    this.searchForTeams();
  }

  customSortFnGM = (a, b) => {
    this.sortValueForTeams = ''
    Object.values(this.mapOfExpandedData).forEach((items: any[]) => {
      items.forEach(item => {
        item.expand = false;
      });
    });
    const aValue = a.gm1 ? a.gm1.value : 0;
    const bValue = b.gm1 ? b.gm1.value : 0;
    return aValue - bValue;
  };
  customSortFnRepositories = (a, b) => {
    this.sortValueForTeams = ''
    Object.values(this.mapOfExpandedData).forEach((items: any[]) => {
      items.forEach(item => {
        item.expand = false;
      });
    });
    const aValue = a.repositoryCount ? a.repositoryCount : 0;
    const bValue = b.repositoryCount ? b.repositoryCount : 0;
    return aValue - bValue;
  };
  customSortFnMembers = (a, b) => {
    this.sortValueForTeams = ''
    Object.values(this.mapOfExpandedData).forEach((items: any[]) => {
      items.forEach(item => {
        item.expand = false;
      });
    });
    const aValue = a.teamActiveProfiles.activeProfileCount ? a.teamActiveProfiles.activeProfileCount : 0;
    const bValue = b.teamActiveProfiles.activeProfileCount ? b.teamActiveProfiles.activeProfileCount : 0;
    return aValue - bValue;
  };

  filterChangeForTeams(e: string[], type: string): void {
    const selectedMapperForTeams = (item: Base.FilterObjectModel): Base.FilterObjectModel => {
      item.selected = e.indexOf(item.text) > -1;
      return item;
    };

    switch (type) {
      case "leads":
        this.listOfLeads.map(selectedMapperForTeams);
        break;
      case "tools":
        this.listOfTools.map(selectedMapperForTeams);
        break;
    }
    this.searchForTeams();
  }

  searchForTeams(): void {
    const initialListDataForTeams = this.teamHealthTeams;

    const filterFunctForTeams = (rowData: any): boolean => {
      // tslint:disable-next-line:no-shadowed-variable
      const filterControlForTeams = (filterList, key, data) => {
        const selectedFilterForTeams = filterList?.filter(item => item.selected);
        if (Array.isArray(data[key])) {
          const keys = data[key].map(tool => tool.addonName);
          const names = data[key].map(lead => lead.name);

          if (selectedFilterForTeams.length) {
            return selectedFilterForTeams.some(filterItem =>
              (keys.length && keys.includes(filterItem.text)) ||
              (names.length && names.includes(filterItem.text))
            );
          } else {
            return true;
          }
        }
      };

      // tslint:disable-next-line:no-shadowed-variable
      const recursiveFilter = (data: any) => {
        if (this.listOfTools !== undefined && this.listOfLeads !== undefined && filterControlForTeams(this.listOfLeads, 'leads', data)
          && filterControlForTeams(this.listOfTools, 'tools', data)
        ) {
          return true;

        } else if (Array.isArray(data.children)) {
          return data.children.some(child => recursiveFilter(child));
        }
        return false;
      };
      return recursiveFilter(rowData);

    }
    const data: any[] = initialListDataForTeams.filter(filterFunctForTeams);

    // tslint:disable-next-line:no-shadowed-variable
    function extractChildrenWithLeadName(data: any, selectedItem) {
      let result = [];
      data.forEach(item => {
        if (item.children && item.children.length > 0) {
          result = result.concat(extractChildrenWithLeadName(item.children, selectedItem));
        }

        if (
          item.leads && item.leads.some(lead => selectedItem.includes(lead.name)) ||
          item.tools && item.tools.some(tool => selectedItem.includes(tool.addonName))
        ) {
          result.push(item);
        }
      });

      return result;
    }

    if (!this.sortValueForTeams?.value) {
      if (this.listOfLeads !== undefined && this.listOfTools !== undefined && !this.sortValueForTeams?.value) {
        const selectedLeadNames = this.listOfLeads.filter(lead => lead.selected).map(lead => lead.text);
        const selectedToolNames = this.listOfTools.filter(tool => tool.selected).map(tool => tool.text);
        const selectedNames = [...selectedLeadNames, ...selectedToolNames];
        if (selectedNames.length) {
          this.teamsDisplayData = extractChildrenWithLeadName(data, selectedNames)
            .map(item => {
              item.developerTooltip = this.generateDeveloperTooltip(item);
              return {
                ...item,
                children: [],
                parentId: [],
                includedTeams: [],
                developerTooltip: item.teamActiveProfiles.activeProfiles.length !== 0
                  ? (item.teamActiveProfiles.activeProfiles.length >= 15
                    ? item.teamActiveProfiles.activeProfiles.slice(0, 15).join('<br>') + '<br><a>More Details</a>'
                    : item.teamActiveProfiles.activeProfiles.join('<br>'))
                  : '',
              };
            });
          const uniqueIds = new Set();
          this.teamsDisplayData = this.teamsDisplayData.filter(item => {
            if (!uniqueIds.has(item.id)) {
              uniqueIds.add(item.id);
              return true;
            }
            return false;
          });
          this.teamsDisplayData.forEach(item => {
            this.mapOfExpandedData[item.key] = this.convertTreeToList(item);

          });
        } else {
          this.teamsDisplayData = data

          this.mapOfExpandedData = {};
          this.teamsDisplayData.forEach(item => {
            this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
          });
        }
      }
      if (this.sortValueForTeams?.value) {
        this.teamsDisplayData = sortFieldsOnTable(this.teamsDisplayData, {
          key: this.sortValueForTeams.key,
          value: this.sortValueForTeams.value
        });
      }
    }
  }

  generateDeveloperTooltip(item: any): string {
    let tooltipContent = 'No members available';
    if (item.teamActiveProfiles?.activeProfiles) {
      const profiles = Array.isArray(item.teamActiveProfiles.activeProfiles) ? item.teamActiveProfiles.activeProfiles : [item.teamActiveProfiles.activeProfiles];
      tooltipContent = '<ul>' + profiles.map(profile => `<li>${profile}</li>`).join('') + '</ul>';
    }
    return tooltipContent;
  }

  showMembersModal(team: any): void {
    let activeProfilesList = 'No members available';
    if (team.activeProfiles) {
      const profiles = Array.isArray(team.activeProfiles) ? team.activeProfiles : [team.activeProfiles];
      activeProfilesList = '<ul>' + profiles.map(profile => `<li>${profile}</li>`).join('') + '</ul>';
    }

    this.modal.create({
      nzTitle: 'All members of the team',
      nzContent: `<div>${activeProfilesList}</div>`,
      nzCancelText: null,
      nzWidth: 380
    });
  }
}
